import { createPortal } from "react-dom";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ChevronDownIcon,
  ChevronUpIcon,
  PlusIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import ReactTooltip from "react-tooltip";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import Drawer from "../Drawer";
import {
  createProductCatalogPresetSetting,
  createProductCatalogCategoryGroup,
  createProductCatalogCategoryVariant,
  createProductCatalogContainerGroup,
  deleteProductCatalogPresetSetting,
  deleteProductCatalogCategoryGroup,
  deleteProductCatalogCategoryVariant,
  deleteProductCatalogContainerGroup,
  fetchProductCatalogCategorySettings,
  fetchProductCatalogPresetSettings,
  fetchProductCatalogContainerSettings,
  ProductCatalogLoadingKeys,
} from "../../slices/productCatalogSlice";
import { arrayHasItems } from "../../helpers/common";
import { productCatalogStatuses } from "../../consts/productCatalog";

const USAGE_COUNTS_URL = "/api/v1/product-catalog/settings/usage-counts";
const USAGE_SKUS_URL = "/api/v1/product-catalog/settings/usage-skus";

const emptyUsageCounts = () => ({
  category_group: {},
  category_variant: {},
  entry_engine: {},
  brand_pillar: {},
  demand_tier: {},
  intent: {},
  container_group: {},
});

/** Matches catalog toolbar filter labels → applied when opening catalog from this modal */
const FILTER_ACTION_LABELS = {
  category_group: "Categories",
  category_variant: "Subcategories",
  entry_engine: "Entry Engines",
  brand_pillar: "Brand Pillars",
  demand_tier: "Demand Tiers",
  intent: "Intents",
  container_group: "Container Groups",
};

function CatalogSettingDeleteChip({
  label,
  usageKind,
  usageId,
  usageCounts,
  onOpenUsageModal,
  onDelete,
}) {
  const n = Number(usageCounts?.[usageKind]?.[String(usageId)] ?? 0);
  const inUse = n > 0;
  const tooltipId = `catalog-setting-delete-tooltip-${usageKind}-${usageId}`;
  return (
    <div className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-gray-50 px-2 py-1 text-xs text-gray-700">
      <span className="min-w-0">{label}</span>
      <button
        type="button"
        title={inUse ? undefined : "Delete"}
        data-tip={inUse}
        data-for={tooltipId}
        aria-label={inUse ? "Cannot delete — in use by products" : "Delete"}
        onClick={() => {
          if (inUse) {
            onOpenUsageModal({ kind: usageKind, id: usageId, label });
            return;
          }
          onDelete();
        }}
        className={`shrink-0 rounded p-0.5 transition ${
          inUse
            ? "text-gray-400 cursor-not-allowed opacity-60"
            : "text-gray-400 hover:text-gray-800"
        }`}
      >
        <XMarkIcon className="h-3.5 w-3.5" />
      </button>
      {inUse ? (
        <ReactTooltip id={tooltipId} effect="solid" place="top" className="text-xs">
          Cannot be deleted. Click for details.
        </ReactTooltip>
      ) : null}
    </div>
  );
}

export const ProductCatalogSettingsSidebar = ({
  isOpen,
  onClose,
  onNavigateToSkuSearch,
  onApplyCatalogSettingFilter,
  /** When set, only that block is shown (hub sub-panels). */
  sectionFilter = null,
  /** When true, body only — parent provides the drawer shell. */
  embedded = false,
}) => {
  const dispatch = useDispatch();
  const categorySettingsItems = useSelector((state) => state.productCatalog.categorySettingsItems);
  const presetSettingsByType = useSelector((state) => state.productCatalog.presetSettingsByType);
  const containerSettingsItems = useSelector((state) => state.productCatalog.containerSettingsItems);
  const loadingKeys = useSelector((state) => state.productCatalog.loadingKeys);
  const [newCategoryGroupName, setNewCategoryGroupName] = useState("");
  const [newContainerGroupName, setNewContainerGroupName] = useState("");
  const [subcategoryInputs, setSubcategoryInputs] = useState({});
  const [presetInputs, setPresetInputs] = useState({
    entry_engine: "",
    brand_pillar: "",
    demand_tier: "",
    intent: "",
  });
  const [openSections, setOpenSections] = useState({
    categories: false,
    enum_statuses: false,
    entry_engine: false,
    brand_pillar: false,
    demand_tier: false,
    intent: false,
    container_group: false,
  });
  const [usageCounts, setUsageCounts] = useState(emptyUsageCounts);
  const [usageModal, setUsageModal] = useState(null);
  const [usageSkus, setUsageSkus] = useState([]);
  const [usageTotal, setUsageTotal] = useState(0);
  const [usageSkusLoading, setUsageSkusLoading] = useState(false);

  const toggleSection = (key) => {
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  useEffect(() => {
    if (!isOpen) return;
    dispatch(fetchProductCatalogCategorySettings());
    dispatch(fetchProductCatalogPresetSettings());
    dispatch(fetchProductCatalogContainerSettings());
  }, [dispatch, isOpen]);

  useEffect(() => {
    if (!isOpen || !sectionFilter) return;
    if (sectionFilter === "categories") {
      setOpenSections((p) => ({ ...p, categories: true }));
    } else if (sectionFilter === "enum_statuses") {
      setOpenSections((p) => ({ ...p, enum_statuses: true }));
    } else if (["entry_engine", "brand_pillar", "demand_tier", "intent"].includes(sectionFilter)) {
      setOpenSections((p) => ({ ...p, [sectionFilter]: true }));
    } else if (sectionFilter === "container_group") {
      setOpenSections((p) => ({ ...p, container_group: true }));
    }
  }, [isOpen, sectionFilter]);

  const catalogDataSignature = useMemo(
    () =>
      JSON.stringify({
        groups: (categorySettingsItems || []).map((g) => ({
          id: g.id,
          v: (g.variants || []).map((x) => x.id),
        })),
        presets: Object.fromEntries(
          Object.entries(presetSettingsByType || {}).map(([k, list]) => [
            k,
            (list || []).map((p) => p.id),
          ])
        ),
        containerGroups: (containerSettingsItems || []).map((g) => g.id),
      }),
    [categorySettingsItems, presetSettingsByType, containerSettingsItems]
  );

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(USAGE_COUNTS_URL);
        if (!cancelled && data) {
          setUsageCounts({
            ...emptyUsageCounts(),
            ...data,
          });
        }
      } catch {
        if (!cancelled) setUsageCounts(emptyUsageCounts());
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, catalogDataSignature]);

  const openUsageModal = useCallback(async ({ kind, id, label }) => {
    setUsageModal({ kind, id, label });
    setUsageSkus([]);
    setUsageTotal(0);
    setUsageSkusLoading(true);
    try {
      const { data } = await axios.get(USAGE_SKUS_URL, {
        params: { kind, id },
      });
      setUsageSkus(data?.skus || []);
      setUsageTotal(Number(data?.total) || 0);
    } catch {
      setUsageSkus([]);
      setUsageTotal(0);
    } finally {
      setUsageSkusLoading(false);
    }
  }, []);

  const closeUsageModal = useCallback(() => {
    setUsageModal(null);
    setUsageSkus([]);
    setUsageTotal(0);
    setUsageSkusLoading(false);
  }, []);

  const handleSkuPick = useCallback(
    (sku) => {
      closeUsageModal();
      onClose?.();
      onNavigateToSkuSearch?.(sku);
    },
    [closeUsageModal, onClose, onNavigateToSkuSearch]
  );

  const handleApplyFilterAndGoToCatalog = useCallback(() => {
    if (!usageModal?.kind || usageModal?.id == null) return;
    const { kind, id } = usageModal;
    closeUsageModal();
    onClose?.();
    onApplyCatalogSettingFilter?.({ kind, id });
  }, [closeUsageModal, onApplyCatalogSettingFilter, onClose, usageModal]);

  const categoriesCount = categorySettingsItems.length;
  const isLoading = loadingKeys.includes(ProductCatalogLoadingKeys.fetchCategorySettings);
  const isPresetLoading = loadingKeys.includes(ProductCatalogLoadingKeys.fetchPresetSettings);
  const isContainerLoading = loadingKeys.includes(ProductCatalogLoadingKeys.fetchContainerSettings);

  const normalizedGroups = useMemo(
    () => categorySettingsItems || [],
    [categorySettingsItems]
  );

  const handleCreateCategoryGroup = async () => {
    const name = newCategoryGroupName.trim();
    if (!name) return;
    await dispatch(createProductCatalogCategoryGroup({ name }));
    setNewCategoryGroupName("");
  };

  const handleCreateSubcategory = async (groupId) => {
    const name = (subcategoryInputs[groupId] || "").trim();
    if (!name) return;
    await dispatch(createProductCatalogCategoryVariant({ groupId, name }));
    setSubcategoryInputs((prev) => ({
      ...prev,
      [groupId]: "",
    }));
  };

  const handleCreatePreset = async (type) => {
    const name = String(presetInputs[type] || "").trim();
    if (!name) return;
    await dispatch(createProductCatalogPresetSetting({ type, name }));
    setPresetInputs((prev) => ({ ...prev, [type]: "" }));
  };

  const handleCreateContainerGroup = async () => {
    const name = newContainerGroupName.trim();
    if (!name) return;
    await dispatch(createProductCatalogContainerGroup({ name }));
    setNewContainerGroupName("");
  };

  const PRESET_TYPE_LABELS = {
    entry_engine: "Entry Engine",
    brand_pillar: "Brand Pillars",
    demand_tier: "Demand Tiers",
    intent: "Intent",
  };

  const usageModalPortal =
    usageModal &&
    createPortal(
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
        <button
          type="button"
          className="absolute inset-0 bg-black/40"
          aria-label="Close"
          onClick={closeUsageModal}
        />
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="catalog-usage-modal-title"
          className="relative z-[101] w-full max-w-md rounded-lg border border-gray-200 bg-white shadow-xl"
        >
          <div className="flex items-start justify-between gap-2 border-b border-gray-100 px-4 py-3">
            <div className="min-w-0">
              <p id="catalog-usage-modal-title" className="text-sm font-semibold text-gray-900">
                Products using this value
              </p>
              <p className="mt-0.5 truncate text-xs text-gray-500">{usageModal.label}</p>
            </div>
            <button
              type="button"
              onClick={closeUsageModal}
              className="shrink-0 rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
          <div className="border-b border-gray-100 bg-gray-50/90 px-4 py-3">
            <p className="text-xs text-gray-600 mb-2">
              Open the product catalog with the{" "}
              <span className="font-medium text-gray-800">
                {FILTER_ACTION_LABELS[usageModal.kind] ?? "matching"}
              </span>{" "}
              filter set to this value. Other filters and search are cleared so the table lists
              every matching product.
            </p>
            <button
              type="button"
              className="w-full rounded-md bg-primary px-3 py-1 text-sm font-semibold text-white shadow-sm transition-opacity hover:opacity-90"
              onClick={handleApplyFilterAndGoToCatalog}
            >
              Filter catalog by this value
            </button>
          </div>
          <div className="max-h-80 overflow-y-auto px-2 py-2">
            {usageSkusLoading ? (
              <p className="px-2 py-6 text-center text-sm text-gray-400">Loading SKUs…</p>
            ) : usageSkus.length === 0 ? (
              <p className="px-2 py-6 text-center text-sm text-gray-400">No SKUs found.</p>
            ) : (
              <ul className="flex flex-col gap-0.5">
                {usageSkus.map((sku) => (
                  <li key={sku}>
                    <button
                      type="button"
                      className="w-full rounded-md px-3 py-2 text-left font-mono text-sm text-gray-800 hover:bg-primary/10 hover:text-primary"
                      onClick={() => handleSkuPick(sku)}
                    >
                      {sku}
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {!usageSkusLoading && usageTotal > usageSkus.length ? (
              <p className="border-t border-gray-100 px-3 py-2 text-xs text-gray-400">
                Showing {usageSkus.length} of {usageTotal} products (max 500). Refine in catalog
                search if needed.
              </p>
            ) : null}
          </div>
          <p className="border-t border-gray-100 px-4 py-2 text-xs text-gray-400">
            Or click a SKU to search by that SKU only (other catalog filters are cleared).
          </p>
        </div>
      </div>,
      document.body
    );

  const showCategoriesSection = !sectionFilter || sectionFilter === "categories";
  const showEnumStatusesSection = !sectionFilter || sectionFilter === "enum_statuses";
  const presetTypesToShow = Object.keys(PRESET_TYPE_LABELS).filter(
    (type) => !sectionFilter || sectionFilter === type
  );
  const showContainerGroupSection = !sectionFilter || sectionFilter === "container_group";
  const containerGroupCount = (containerSettingsItems || []).length;
  const containerGroupSectionOpen = openSections.container_group;

  const mainContent = (
    <>
      {usageModalPortal}
      <div className="flex flex-col gap-3">
        
        {showCategoriesSection && (
        <section className="rounded-md border border-gray-200 bg-white overflow-hidden">
          <button
            type="button"
            onClick={() => toggleSection("categories")}
            className="w-full px-4 py-3 text-left bg-gray-50 hover:bg-gray-100 transition-all duration-200"
          >
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 min-w-0">
                <p className="text-sm font-medium text-gray-900">Categories</p>
                <span className="rounded border border-gray-200 px-2 py-0.5 text-xs text-gray-500">
                  {categoriesCount}
                </span>
              </div>
              {openSections.categories ? (
                <ChevronUpIcon className="h-4 w-4 shrink-0 text-gray-400" />
              ) : (
                <ChevronDownIcon className="h-4 w-4 shrink-0 text-gray-400" />
              )}
            </div>
          </button>

          {openSections.categories && (
          <div className="px-4 py-4 flex flex-col gap-5">
            {arrayHasItems(normalizedGroups) && (
              <div className="flex flex-wrap gap-2">
                {normalizedGroups.map((group) => (
                  <CatalogSettingDeleteChip
                    key={group.id}
                    label={group.name}
                    usageKind="category_group"
                    usageId={group.id}
                    usageCounts={usageCounts}
                    onOpenUsageModal={openUsageModal}
                    onDelete={() => dispatch(deleteProductCatalogCategoryGroup({ id: group.id }))}
                  />
                ))}
              </div>
            )}
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={newCategoryGroupName}
                onChange={(event) => setNewCategoryGroupName(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    handleCreateCategoryGroup();
                  }
                }}
                placeholder="Add to Categories..."
                className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <button
                type="button"
                onClick={handleCreateCategoryGroup}
                disabled={!newCategoryGroupName.trim()}
                className="rounded-md bg-primary p-2 text-white disabled:cursor-not-allowed disabled:opacity-50"
              >
                <PlusIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="flex flex-col gap-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Subcategories
              </p>
              {normalizedGroups.map((group) => (
                <section key={group.id} className="flex flex-col gap-2">
                  <p className="text-sm font-medium text-gray-700">{group.name}</p>
                  <div className="flex flex-wrap gap-2">
                    {(group.variants || []).length > 0 ? (
                      group.variants.map((variant) => (
                        <CatalogSettingDeleteChip
                          key={variant.id}
                          label={variant.name}
                          usageKind="category_variant"
                          usageId={variant.id}
                          usageCounts={usageCounts}
                          onOpenUsageModal={openUsageModal}
                          onDelete={() =>
                            dispatch(deleteProductCatalogCategoryVariant({ id: variant.id }))
                          }
                        />
                      ))
                    ) : (
                      <p className="text-xs italic text-gray-400">None</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={subcategoryInputs[group.id] || ""}
                      onChange={(event) =>
                        setSubcategoryInputs((prev) => ({
                          ...prev,
                          [group.id]: event.target.value,
                        }))
                      }
                      onKeyDown={(event) => {
                        if (event.key === "Enter") {
                          event.preventDefault();
                          handleCreateSubcategory(group.id);
                        }
                      }}
                      placeholder={`Add subcategory under ${group.name}...`}
                      className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                    <button
                      type="button"
                      onClick={() => handleCreateSubcategory(group.id)}
                      disabled={!(subcategoryInputs[group.id] || "").trim()}
                      className="rounded-md bg-primary p-2 text-white disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <PlusIcon className="h-5 w-5" />
                    </button>
                  </div>
                </section>
              ))}
            </div>

            {isLoading && (
              <p className="text-xs text-gray-400">Loading category settings...</p>
            )}
          </div>
          )}
        </section>
        )}

        {showEnumStatusesSection && (
        <section className="rounded-md border border-gray-200 bg-white overflow-hidden">
          <button
            type="button"
            onClick={() => toggleSection("enum_statuses")}
            className="w-full px-4 py-3 text-left bg-gray-50 hover:bg-gray-100 transition-all duration-200"
          >
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 min-w-0">
                <p className="text-sm font-medium text-gray-900">Statuses</p>
                <span className="rounded border border-gray-200 px-2 py-0.5 text-xs text-gray-500">
                  {productCatalogStatuses.length}
                </span>
              </div>
              {openSections.enum_statuses ? (
                <ChevronUpIcon className="h-4 w-4 shrink-0 text-gray-400" />
              ) : (
                <ChevronDownIcon className="h-4 w-4 shrink-0 text-gray-400" />
              )}
            </div>
          </button>
          {openSections.enum_statuses && (
            <div className="border-t border-gray-200 px-4 py-4 bg-gray-50/40">
              <div className="flex flex-wrap gap-2">
                {productCatalogStatuses.map((item) => (
                  <span
                    key={item.value}
                    className="inline-flex items-center rounded-md border border-gray-200 bg-white px-2 py-1 text-xs text-gray-700"
                  >
                    {item.label}
                  </span>
                ))}
              </div>
            </div>
          )}
        </section>
        )}

        {presetTypesToShow.length > 0 && (
        <section className="flex flex-col gap-3">
          <div className="flex flex-col gap-3">
            {presetTypesToShow.map((type) => {
              const presetList = presetSettingsByType?.[type] || [];
              const count = presetList.length;
              const sectionOpen = openSections[type];
              return (
                <div
                  key={type}
                  className="rounded-md border border-gray-200 bg-white overflow-hidden"
                >
                  <button
                    type="button"
                    onClick={() => toggleSection(type)}
                    className="w-full px-4 py-3 text-left bg-gray-50 hover:bg-gray-100 transition-all duration-200 flex items-center justify-between gap-3"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {PRESET_TYPE_LABELS[type]}
                      </p>
                      <span className="rounded border border-gray-200 px-2 py-0.5 text-xs text-gray-500 shrink-0">
                        {count}
                      </span>
                    </div>
                    {sectionOpen ? (
                      <ChevronUpIcon className="h-4 w-4 shrink-0 text-gray-400" />
                    ) : (
                      <ChevronDownIcon className="h-4 w-4 shrink-0 text-gray-400" />
                    )}
                  </button>
                  {sectionOpen && (
                    <div className="border-t border-gray-200 px-4 py-4 flex flex-col gap-3 bg-gray-50/40">
                      <div className="flex flex-wrap gap-2">
                        {count > 0 ? (
                          presetList.map((preset) => (
                            <CatalogSettingDeleteChip
                              key={preset.id}
                              label={preset.name}
                              usageKind={type}
                              usageId={preset.id}
                              usageCounts={usageCounts}
                              onOpenUsageModal={openUsageModal}
                              onDelete={() =>
                                dispatch(deleteProductCatalogPresetSetting({ id: preset.id }))
                              }
                            />
                          ))
                        ) : (
                          <p className="text-xs italic text-gray-400">None</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={presetInputs[type] || ""}
                          onChange={(event) =>
                            setPresetInputs((prev) => ({
                              ...prev,
                              [type]: event.target.value,
                            }))
                          }
                          onKeyDown={(event) => {
                            if (event.key === "Enter") {
                              event.preventDefault();
                              handleCreatePreset(type);
                            }
                          }}
                          placeholder={`Add to ${PRESET_TYPE_LABELS[type]}...`}
                          className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary"
                        />
                        <button
                          type="button"
                          onClick={() => handleCreatePreset(type)}
                          disabled={!(presetInputs[type] || "").trim()}
                          className="rounded-md bg-primary p-2 text-white disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <PlusIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
            {isPresetLoading && (
              <p className="text-xs text-gray-400 px-1">Loading preset settings...</p>
            )}
          </div>
        </section>
        )}

        {showContainerGroupSection && (
        <section className="rounded-md border border-gray-200 bg-white overflow-hidden">
          <button
            type="button"
            onClick={() => toggleSection("container_group")}
            className="w-full px-4 py-3 text-left bg-gray-50 hover:bg-gray-100 transition-all duration-200 flex items-center justify-between gap-3"
          >
            <div className="flex items-center gap-2 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">Container Groups</p>
              <span className="rounded border border-gray-200 px-2 py-0.5 text-xs text-gray-500 shrink-0">
                {containerGroupCount}
              </span>
            </div>
            {containerGroupSectionOpen ? (
              <ChevronUpIcon className="h-4 w-4 shrink-0 text-gray-400" />
            ) : (
              <ChevronDownIcon className="h-4 w-4 shrink-0 text-gray-400" />
            )}
          </button>
          {containerGroupSectionOpen && (
            <div className="border-t border-gray-200 px-4 py-4 flex flex-col gap-3 bg-gray-50/40">
              <div className="flex flex-wrap gap-2">
                {containerGroupCount > 0 ? (
                  containerSettingsItems.map((group) => (
                    <CatalogSettingDeleteChip
                      key={group.id}
                      label={group.name}
                      usageKind="container_group"
                      usageId={group.id}
                      usageCounts={usageCounts}
                      onOpenUsageModal={openUsageModal}
                      onDelete={() =>
                        dispatch(deleteProductCatalogContainerGroup({ id: group.id }))
                      }
                    />
                  ))
                ) : (
                  <p className="text-xs italic text-gray-400">None</p>
                )}
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={newContainerGroupName}
                  onChange={(event) => setNewContainerGroupName(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      handleCreateContainerGroup();
                    }
                  }}
                  placeholder="Add to Container Groups..."
                  className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary"
                />
                <button
                  type="button"
                  onClick={handleCreateContainerGroup}
                  disabled={!newContainerGroupName.trim()}
                  className="rounded-md bg-primary p-2 text-white disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <PlusIcon className="h-5 w-5" />
                </button>
              </div>
              {isContainerLoading && (
                <p className="text-xs text-gray-400">Loading container groups...</p>
              )}
            </div>
          )}
        </section>
        )}
      </div>
    </>
  );

  if (embedded) {
    return isOpen ? mainContent : null;
  }

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title="Catalog Settings"
      width="500px"
    >
      {mainContent}
    </Drawer>
  );
};
